import React, { useState, useRef, useEffect } from 'react';
import { Calendar as CalIcon, X } from 'lucide-react';

// Helpers (no timezone conversion)
function isoToBR(iso) {
  if (!iso) return '';
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
}
function brToIso(br) {
  const m = String(br).match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return null;
  const dd = parseInt(m[1]), mm = parseInt(m[2]), yyyy = parseInt(m[3]);
  if (mm < 1 || mm > 12 || dd < 1 || dd > 31 || yyyy < 1900) return null;
  return `${yyyy}-${String(mm).padStart(2,'0')}-${String(dd).padStart(2,'0')}`;
}
function parseIsoToDate(iso) {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return new Date();
  return new Date(parseInt(m[1]), parseInt(m[2])-1, parseInt(m[3]));
}
function dateToIso(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}
function applyMask(raw) {
  const digits = raw.replace(/\D/g, '').slice(0, 8);
  const a = digits.slice(0, 2);
  const b = digits.slice(2, 4);
  const c = digits.slice(4, 8);
  let out = a;
  if (digits.length >= 3) out += '/' + b;
  if (digits.length >= 5) out += '/' + c;
  return out;
}

export default function DateInput({ value, onChange, required, placeholder = 'dd/mm/aaaa', className = '' }) {
  const [text, setText] = useState(isoToBR(value));
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => { setText(isoToBR(value)); }, [value]);

  useEffect(() => {
    const click = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', click);
    return () => document.removeEventListener('mousedown', click);
  }, []);

  const commit = (br) => {
    setText(br);
    const iso = brToIso(br);
    if (iso) onChange?.(iso);
    else if (!br) onChange?.('');
  };

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <div className="relative">
        <input
          type="text"
          inputMode="numeric"
          value={text}
          onChange={(e) => commit(applyMask(e.target.value))}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          required={required}
          className="input-md pr-9"
        />
        <button type="button" onClick={() => setOpen((o) => !o)}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-500 dark:text-slate-400 hover:text-indigo-600"
          tabIndex={-1}>
          <CalIcon className="w-4 h-4" />
        </button>
      </div>
      {open && (
        <MiniCalendar
          value={value}
          onSelect={(iso) => { onChange?.(iso); setText(isoToBR(iso)); setOpen(false); }}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}

const WEEKDAYS = ['D','S','T','Q','Q','S','S'];
const MONTHS = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];

function MiniCalendar({ value, onSelect, onClose }) {
  const initial = value ? parseIsoToDate(value) : new Date();
  const [view, setView] = useState({ y: initial.getFullYear(), m: initial.getMonth() });
  const todayIso = dateToIso(new Date());

  const first = new Date(view.y, view.m, 1);
  const startWeekDay = first.getDay();
  const daysInMonth = new Date(view.y, view.m + 1, 0).getDate();

  const cells = [];
  for (let i = 0; i < startWeekDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const move = (delta) => {
    let m = view.m + delta;
    let y = view.y;
    while (m < 0) { m += 12; y -= 1; }
    while (m > 11) { m -= 12; y += 1; }
    setView({ y, m });
  };

  return (
    <div className="absolute z-50 mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl p-3 w-72">
      <div className="flex items-center justify-between mb-2">
        <button type="button" onClick={() => move(-1)} className="px-2 py-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300">‹</button>
        <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">{MONTHS[view.m]} {view.y}</div>
        <button type="button" onClick={() => move(1)} className="px-2 py-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300">›</button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-xs text-slate-400 mb-1">
        {WEEKDAYS.map((w,i) => <div key={i} className="text-center py-0.5">{w}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (!d) return <div key={i} />;
          const iso = `${view.y}-${String(view.m+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
          const isSelected = iso === value;
          const isToday = iso === todayIso;
          return (
            <button key={i} type="button" onClick={() => onSelect(iso)}
              className={`text-sm rounded-md py-1 ${isSelected ? 'bg-gradient-to-br from-indigo-600 to-blue-500 text-white font-semibold' : isToday ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 font-medium' : 'text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700'}`}>
              {d}
            </button>
          );
        })}
      </div>
      <div className="flex gap-2 mt-3 pt-2 border-t border-slate-100 dark:border-slate-700">
        <button type="button" onClick={() => onSelect(todayIso)}
          className="flex-1 text-xs py-1.5 rounded-md text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/30">Hoje</button>
        <button type="button" onClick={() => { onSelect(''); onClose(); }}
          className="flex-1 text-xs py-1.5 rounded-md text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700">Limpar</button>
      </div>
    </div>
  );
}
